import React, { useEffect, useState } from "react";
import { Box, Grid, Button, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Helmet, PageTitle } from "@app/components/atoms";
import { InputText } from "primereact/inputtext";
import { apiSearchUsers } from "@app/api";
import SearchUserTable from "./Components/SearchUserTable";
import { useLocation, useSearchParams } from "react-router-dom";

type UserSearchResult = {
  userId?: string;
  email?: string;
  givenName?: string;
  surname?: string;
  currentStatus?: string;
  [key: string]: any;
};

export function SearchUser() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [searchText, setSearchText] = useState<string>("");
  const [results, setResults] = useState<UserSearchResult[]>();
  const [loading, setLoading] = useState<boolean>(true);

  const clearSearch = () => {
    setSearchText("");
    setResults([]);
  };

  const handleSearch = async () => {
    const q = searchText.trim();
    if (!q) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiSearchUsers(q);
      setResults(res.data?.data || []);
    } catch (err) {
      console.error("Failed to search users:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const mode = searchParams.get("mode");
  const isManageMode = mode === "manage";
  const titleKey = isManageMode ? "metaInfo.manageUser" : "metaInfo.searchUser";

  const titleKeyPage = isManageMode
    ? "permission.USER_MANAGE"
    : "permission.USER_SEARCH";

  useEffect(() => {
    setSearchText("");
    setResults([]);
    setLoading(false);
  }, [mode, location.pathname]);

  return (
    <Box
      className="container"
      sx={{ mx: { xs: 2, sm: 3, md: 5, xl: "auto" }, px: "24px" }}
    >
      <Helmet title={t(titleKey)} />

      <Box sx={{ pt: { xs: 1, sm: 4 }, pb: 4, margin: "auto" }}>
        <Grid container>
          <Grid item xs={12}>
            <PageTitle title={titleKeyPage} className="mb-16" />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={6}
            sx={{ mt: { xs: 1, sm: 3 } }}
          >
            <Box
              className="input-field"
              sx={{
                display: "flex",
                flexDirection: isSmallScreen ? "column" : "row",
                alignItems: isSmallScreen ? "stretch" : "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <InputText
                placeholder={t("permission.USER_SEARCH") as string}
                value={searchText}
                onChange={(e: any) => {
                  const value = e.target.value;
                  setSearchText(value);

                  if (!value.trim()) {
                    setResults([]);
                  }
                }}
                onKeyDown={handleKeyDown}
                className="custom-input"
                style={{
                  width: "100%",
                  paddingLeft: "1rem",
                  paddingRight: "1rem",
                  height: "50px",
                  border: "1.5px solid #bcbcbc",
                  borderRadius: "30px",
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isSmallScreen ? "column" : "row",
                  gap: 1,
                }}
              >
                <Button
                  onClick={handleSearch}
                  className="btn btn-primary"
                  fullWidth={isSmallScreen}
                >
                  {t("searchMember")}
                </Button>

                <Button
                  onClick={clearSearch}
                  className="btn btn-no-border"
                  fullWidth={isSmallScreen}
                >
                  {t("clear")}
                </Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ mt: 1.5 }}>
            <SearchUserTable
              rows={results}
              loading={loading}
              isManageMode={isManageMode}
              onUserRestored={handleSearch}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
